import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

const FOLDER_PERMISSIONS_KEY = "__folder_permissions__";


interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    file: File,
    assetId: string,
    category: string,
    year: string,
    month: string,
    date: string
  ) => void;
  assets: { assetId: string; assetName: string }[]; 
  apiData: Record<string, any>;
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  isOpen,
  onClose,
  onUpload,
  assets,
  apiData,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  // const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [categories, setCategories] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const hasPermissionsSection = Boolean(apiData?.[FOLDER_PERMISSIONS_KEY]);

  const writableAssets = useMemo(() => {
    const permissions = apiData?.[FOLDER_PERMISSIONS_KEY];
    if (!permissions) return assets;

    return assets.filter(({ assetId }) => {
      const assetPermissions = permissions?.[assetId] ?? {};
      return Object.values(assetPermissions).some((permission: any) => Boolean(permission?.writable));
    });
  }, [assets, apiData]);

  useEffect(() => {
    if (!selectedAssetId) return;
    const stillValid = writableAssets.some((asset) => asset.assetId === selectedAssetId);
    if (!stillValid) {
      setSelectedAssetId(null);
      setSelectedCategory(null);
      setSelectedDate(null);
    }
  }, [selectedAssetId, writableAssets]);


  
  useEffect(() => {
    if (!selectedAssetId || !apiData) {
      setCategories([]);
      setSelectedCategory(null);
      return;
    }

    const permissionsForAsset = apiData?.[FOLDER_PERMISSIONS_KEY]?.[selectedAssetId] ?? {};
    const writableCategories = Object.entries(permissionsForAsset)
      .filter(([, permission]: any) => Boolean(permission?.writable))
      .map(([categoryName]) => categoryName)
      .sort();

    if (hasPermissionsSection) {
      setCategories(writableCategories);
      setSelectedCategory((prev) =>
        prev && writableCategories.includes(prev) ? prev : null
      );
      return;
    }

    const assetCategories = Object.keys(apiData[selectedAssetId] || {}).filter(
      (categoryName) => categoryName !== FOLDER_PERMISSIONS_KEY
    );
    setCategories(assetCategories);
    setSelectedCategory((prev) =>
      prev && assetCategories.includes(prev) ? prev : null
    );
  }, [apiData, selectedAssetId]);

 
  useEffect(() => {
    if (selectedCategory && apiData) {
      const yrSet = new Set<string>();
      Object.values(apiData).forEach((assetObj: any) => {
        const catData = assetObj[selectedCategory];
        if (catData) Object.keys(catData).forEach((y) => yrSet.add(y));
      });
      setYears(Array.from(yrSet).sort());
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDate(null);
    }
  }, [selectedCategory, apiData]);


  useEffect(() => {
    if (selectedCategory && selectedYear && apiData) {
      const monthSet = new Set<string>();
      Object.values(apiData).forEach((assetObj: any) => {
        const catData = assetObj[selectedCategory];
        if (catData?.[selectedYear])
          Object.keys(catData[selectedYear]).forEach((m) => monthSet.add(m));
      });
      setMonths(Array.from(monthSet).sort());
      setSelectedMonth(null);
      setSelectedDate(null);
    }
  }, [selectedYear, selectedCategory, apiData]);


  useEffect(() => {
    if (selectedCategory && selectedYear && selectedMonth && apiData) {
      const dateSet = new Set<string>();
      Object.values(apiData).forEach((assetObj: any) => {
        const catData = assetObj[selectedCategory];
        if (catData?.[selectedYear]?.[selectedMonth])
          Object.keys(catData[selectedYear][selectedMonth]).forEach((d) => dateSet.add(d));
      });
      setDates(Array.from(dateSet).sort());
      setSelectedDate(null);
    }
  }, [selectedMonth, selectedYear, selectedCategory, apiData]);

  // const handleUploadClick = () => {
  //   if (!selectedFile) return alert("Please select a file.");
  //   if (!selectedAssetId || !selectedCategory || !selectedYear || !selectedMonth || !selectedDate)
  //     return alert("Please select all dropdown fields before uploading.");

  //   onUpload(selectedFile, selectedAssetId, selectedCategory, selectedYear, selectedMonth, selectedDate);
  //   setSelectedFile(null);
  //   onClose();
  // };
  const handleUploadClick = () => {
    if (!selectedFile) return alert("Please select a file.");
    if (!selectedAssetId || !selectedCategory || !selectedDate)
      return alert("Please select all fields before uploading.");
  
    // Convert Date to YYYY/MM/DD
    const year = selectedDate.getFullYear().toString();
    const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
    const day = selectedDate.getDate().toString().padStart(2, "0");
  
    onUpload(selectedFile, selectedAssetId, selectedCategory, year, month, day);
  
    setSelectedFile(null);
    setSelectedDate(null);
    onClose();
  };

  const isUploadDisabled =
    (hasPermissionsSection && writableAssets.length === 0) ||
    !selectedFile ||
    !selectedAssetId ||
    !selectedCategory ||
    !selectedDate;
  
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 className="text-lg font-semibold mb-4">Upload File</h2>

       
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Asset</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={selectedAssetId || ""}
            disabled={hasPermissionsSection && writableAssets.length === 0}
            onChange={(e) => {
              const nextAssetId = e.target.value || null;
              setSelectedAssetId(nextAssetId);
              setSelectedCategory(null);
              setSelectedDate(null);
            }}
          >
            <option value="">Select Asset</option>
            {writableAssets.map((a) => (
              <option key={a.assetId} value={a.assetId}>
                {a.assetName}
              </option>
            ))}
          </select>
          {hasPermissionsSection && writableAssets.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              No writable assets available for upload.
            </p>
          )}
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Category</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={!selectedAssetId}
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
  <label className="block text-sm font-medium mb-1">As of Date</label>
  <DatePicker
    selected={selectedDate}
    onChange={(date: Date | null) => setSelectedDate(date)}
    dateFormat="yyyy-MM-dd"
    className="w-full border rounded px-2 py-1"
    placeholderText="Select a date"
  />
</div>


        {/* <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Year</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">Select Year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

    
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Month</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={selectedMonth || ""}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Select Month</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

   
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Date</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={selectedDate || ""}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            <option value="">Select Date</option>
            {dates.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

     */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">File</label>
          <input
            type="file"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
        </div>

 
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            onClick={handleUploadClick}
            disabled={isUploadDisabled}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDialog;
